// Story 2.19: Export to JSON Modal Component

import React, { useState } from 'react';
import { X, Download, CheckCircle, AlertCircle } from 'lucide-react';
import type { Dashboard } from '../services/dashboard-api.service';
import { generateExportJSON, downloadJSON, generateExportFilename, countWidgets } from '../utils/export.utils';

interface ExportModalProps {
  dashboard: Dashboard;
  userId: string;
  onClose: () => void;
}

export function ExportModal({ dashboard, userId: _userId, onClose }: ExportModalProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const pageCount = dashboard.pages.length;
  const widgetCount = countWidgets(dashboard);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      // Generate export JSON
      const exportData = generateExportJSON(dashboard);

      // Generate filename
      const filename = generateExportFilename(dashboard.name);

      // Download file
      downloadJSON(exportData, filename);

      // Show success state
      setSuccess(true);

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export dashboard. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Download size={20} className="text-blue-600" />
            Export Dashboard
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isExporting}
          >
            <X size={20} />
          </button>
        </div>

        {/* Success State */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
            <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
            <span className="text-sm text-green-700 dark:text-green-300">
              Dashboard exported successfully!
            </span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
            <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          </div>
        )}

        {/* Dashboard Info */}
        {!success && (
          <>
            <div className="mb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Dashboard:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {dashboard.name}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Pages:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Widgets:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {widgetCount} {widgetCount === 1 ? 'widget' : 'widgets'}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Format:</span>
                <span className="font-medium text-gray-900 dark:text-white">JSON</span>
              </div>
            </div>

            {/* Info Note */}
            <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Export includes all pages, widgets, and configurations. The JSON file can be imported later to restore this dashboard.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                disabled={isExporting}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
